import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { BookingForm } from "@/components/booking-form";
import { RelatedLinks } from "@/components/related-links";
import { sessionDetails, TODO, ONE_PRICE_FALLBACK, STATIONARY_LOCATION, GROUP_HOURS_LABEL } from "@/content/booking";
import portrait from "@/assets/sylwia-portrait.jpg.asset.json";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs, breadcrumbSchema, PERSON_ID, SITE_URL } from "@/content/seo";

const title = "Rezerwacja konsultacji indywidualnej | Sylwia Sitkowska";
const description =
  "Zarezerwuj pierwszą konsultację indywidualną: wybierz formę spotkania i wyślij zgłoszenie. 90 minut, 1 000 zł brutto (usługa zwolniona z VAT).";
const ogImage = abs(ogSocial);

export const Route = createFileRoute("/rezerwacja")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/rezerwacja" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://sylwiasitkowska.pl/rezerwacja" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${SITE_URL}/rezerwacja`,
          url: `${SITE_URL}/rezerwacja`,
          name: title,
          description,
          inLanguage: "pl-PL",
          about: { "@id": PERSON_ID },
          potentialAction: {
            "@type": "ReserveAction",
            name: "Zarezerwuj pierwszą konsultację indywidualną",
            target: `${SITE_URL}/rezerwacja`,
            result: {
              "@type": "Reservation",
              name: "Konsultacja indywidualna (90 minut)",
              provider: { "@id": PERSON_ID },
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([
            { name: "Strona główna", path: "/" },
            { name: "Konsultacje indywidualne", path: "/konsultacje" },
            { name: "Rezerwacja", path: "/rezerwacja" },
          ]),
        ),
      },
    ],
  }),
  component: RezerwacjaPage,
});

const kroki = [
  "Wypełniasz formularz: forma spotkania i krótki opis sytuacji.",
  "Proponuję termin i wysyłam dane do płatności.",
  `Otrzymujesz link do spotkania online albo adres spotkania stacjonarnego. Spotkania stacjonarne odbywają się w: ${STATIONARY_LOCATION}.`,
];


function RezerwacjaPage() {
  const practical = sessionDetails.map((item) => ({
    label: item.label,
    value: item.value ?? (item.label === "Cena" ? ONE_PRICE_FALLBACK : TODO),
  }));

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Rezerwacja, konsultacje indywidualne</p>
            <h1 className="serif-display mt-8 max-w-3xl text-[2.2rem] md:text-[3.2rem]">
              Zarezerwuj pierwsze spotkanie
            </h1>
            <div className="mt-10 h-px w-full bg-steel" />
            <p className="measure mt-8 text-base leading-relaxed text-ink/80 md:text-lg">
              Spotkanie trwa 90 minut i jest pełnoprawną, płatną sesją, nie zobowiązuje Cię do
              dalszej współpracy. Wybierz formę spotkania, a termin ustalimy osobiście. Mastermind dla liderów
              to osobny cykl: 9 spotkań i {GROUP_HOURS_LABEL}.
            </p>

            <dl className="mt-10 grid gap-px border border-steel bg-steel sm:grid-cols-2">
              {practical.map((item) => (
                <div key={item.label} className="bg-background p-5">
                  <dt className="text-xs uppercase tracking-[0.16em] text-ink-soft">
                    {item.label}
                  </dt>
                  <dd className="mt-2 text-base leading-relaxed text-ink/80">{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="border-b border-steel bg-surface">
          <div className="container-page grid gap-10 py-12 md:grid-cols-[0.4fr_1fr] md:gap-16 md:py-16">
            <div>
              <p className="eyebrow">Jak przebiega rezerwacja</p>
            </div>
            <ol className="max-w-2xl border-t border-steel">
              {kroki.map((krok, i) => (
                <li
                  key={krok}
                  className="grid gap-2 border-b border-steel py-5 md:grid-cols-[3rem_1fr]"
                >
                  <span className="font-display text-sm text-ink-soft">{`0${i + 1}`}</span>
                  <span className="text-base leading-relaxed text-ink/80">{krok}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <p className="eyebrow">Formularz rezerwacji</p>
            <h2 className="display-xl mt-8 text-[1.9rem] md:text-[2.4rem]">Wyślij zgłoszenie</h2>
            <div className="mt-10">
              <BookingForm />
            </div>
            <p className="mt-10 text-base leading-relaxed text-ink/80">
              Masz pytanie przed rezerwacją?{" "}
              <Link to="/kontakt" className="border-b border-coral text-ink hover:text-coral-text">
                Napisz do mnie
              </Link>{" "}
              albo wróć do opisu{" "}
              <Link
                to="/konsultacje"
                className="border-b border-coral text-ink hover:text-coral-text"
              >
                Konsultacje indywidualne
              </Link>
              .
            </p>
          </div>
        </section>
      </main>
      <RelatedLinks
        exclude={["/rezerwacja"]}
        only={["/konsultacje", "/mastermind", "/kontakt"]}
        heading="Zanim zarezerwujesz"
      />
      <SiteFooter />
    </div>
  );
}
